import React, { useState, useEffect } from 'react';
import usePlatformStats from '../hooks/usePlatformStats';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import PageHeader from '../components/PageHeader';
import { formatDateTime } from '../utils/formatDate';
import styles from './StatsPage.module.css';

const StatsPage = () => {
  useDocumentTitle('Platform stats');
  const platformStats = usePlatformStats({ enabled: true, includeFeeds: true });
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterEnabled, setFilterEnabled] = useState(null);

  useEffect(() => {
    if (platformStats.feeds) {
      setFeeds(platformStats.feeds);
      setLoading(false);
      setError(platformStats.feedsError ? `Failed to load feed statistics: ${platformStats.feedsError}` : null);
      return;
    }

    if (!platformStats.loading && platformStats.feedsError) {
      setError(`Failed to load feed statistics: ${platformStats.feedsError}`);
      setLoading(false);
    } else if (!platformStats.loading && !platformStats.feeds) {
      setLoading(false);
    } else if (platformStats.loading) {
      setLoading(true);
    }
  }, [
    platformStats.feeds,
    platformStats.feedsError,
    platformStats.loading,
  ]);

  const fetchFeeds = () => {
    platformStats.refetch();
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'Never') {
      return 'Never';
    }
    try {
      return formatDateTime(dateString);
    } catch (e) {
      return dateString;
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const sortedAndFilteredFeeds = [...feeds]
    .filter((feed) => {
      if (filterEnabled === null) return true;
      return feed.enabled === filterEnabled;
    })
    .sort((a, b) => {
      let aVal;
      let bVal;

      switch (sortBy) {
        case 'articleCount':
          aVal = a.articleCount || 0;
          bVal = b.articleCount || 0;
          break;
        case 'category':
          aVal = (a.category || '').toLowerCase();
          bVal = (b.category || '').toLowerCase();
          break;
        case 'lastSuccess':
          aVal = a.lastSuccessfulFetch ? new Date(a.lastSuccessfulFetch).getTime() : 0;
          bVal = b.lastSuccessfulFetch ? new Date(b.lastSuccessfulFetch).getTime() : 0;
          break;
        case 'name':
        default:
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  const totalArticles =
    platformStats.totalArticles ??
    feeds.reduce((sum, feed) => sum + (feed.articleCount || 0), 0);
  const enabledFeeds =
    platformStats.activeSources ?? feeds.filter((f) => f.enabled).length;
  const disabledFeeds = feeds.length - enabledFeeds;

  if (error) {
    return (
      <div className={`more-page ${styles['stats-page']}`}>
        <div className="container">
          <div className={styles['error-message']}>
            <h3>Error Loading Statistics</h3>
            <p>{error}</p>
            <button onClick={fetchFeeds} className="btn btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`more-page ${styles['stats-page']}`}>
      <div className="container">
        <PageHeader
          title="Feed Statistics"
          subtitle="Overview of RSS feed performance and article counts"
        />

        <div className={styles['summary-cards']}>
          <div className={styles['summary-card']}>
            <div className={styles['summary-value']}>{feeds.length}</div>
            <div className={styles['summary-label']}>Total Feeds</div>
          </div>
          <div className={styles['summary-card']}>
            <div className={styles['summary-value']}>{enabledFeeds}</div>
            <div className={styles['summary-label']}>Enabled</div>
          </div>
          <div className={styles['summary-card']}>
            <div className={styles['summary-value']}>{disabledFeeds}</div>
            <div className={styles['summary-label']}>Disabled</div>
          </div>
          <div className={styles['summary-card']}>
            <div className={styles['summary-value']}>
              {totalArticles.toLocaleString()}
            </div>
            <div className={styles['summary-label']}>Total Articles</div>
          </div>
        </div>

        <div className={styles['filters']}>
          <div className={styles['filter-group']}>
            <label>Filter:</label>
            <button
              className={`btn ${filterEnabled === null ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setFilterEnabled(null)}
            >
              All
            </button>
            <button
              className={`btn ${filterEnabled === true ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setFilterEnabled(true)}
            >
              Enabled Only
            </button>
            <button
              className={`btn ${filterEnabled === false ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setFilterEnabled(false)}
            >
              Disabled Only
            </button>
          </div>
        </div>

        {loading ? (
          <div className={styles['loading']}>
            <div className={styles['loading-spinner']} />
            <p className="mt-3">Loading feed statistics...</p>
          </div>
        ) : (
          <div className={styles['table-container']}>
            <table className={styles['stats-table']}>
              <thead>
                <tr>
                  <th
                    className={styles['sortable']}
                    onClick={() => handleSort('name')}
                  >
                    Feed Name {getSortIcon('name')}
                  </th>
                  <th
                    className={styles['sortable']}
                    onClick={() => handleSort('articleCount')}
                  >
                    Articles {getSortIcon('articleCount')}
                  </th>
                  <th
                    className={styles['sortable']}
                    onClick={() => handleSort('category')}
                  >
                    Category {getSortIcon('category')}
                  </th>
                  <th
                    className={styles['sortable']}
                    onClick={() => handleSort('lastSuccess')}
                  >
                    Last Success {getSortIcon('lastSuccess')}
                  </th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sortedAndFilteredFeeds.length === 0 ? (
                  <tr>
                    <td colSpan="5" className={styles['no-data']}>
                      No feeds found
                    </td>
                  </tr>
                ) : (
                  sortedAndFilteredFeeds.map((feed) => (
                    <tr key={feed.id}>
                      <td className={styles['feed-name']}>{feed.name || 'Unknown'}</td>
                      <td className={styles['article-count']}>
                        {feed.articleCount || 0}
                      </td>
                      <td className={styles['category']}>
                        {feed.category ? (
                          <span className="badge bg-secondary">{feed.category}</span>
                        ) : (
                          <span className={styles['no-category']}>—</span>
                        )}
                      </td>
                      <td className={styles['last-success']}>
                        {formatDate(feed.lastSuccessfulFetch)}
                      </td>
                      <td className={styles['status']}>
                        {feed.enabled ? (
                          <span className="badge bg-success">Enabled</span>
                        ) : (
                          <span className="badge bg-secondary">Disabled</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsPage;
